import type { ContractVersionPageQueryParams } from './types';
import type { ContractArticleSuggestionsQueryParams } from './useQueryContractArticleSuggestions';
import type { ContractConditionNameSuggestionsQueryParams } from './useQueryContractConditionNameSuggestions';
import type { ContractDuplicateCheckParams } from './useQueryContractDuplicateCheck';
import type { ContractItemNetPriceQueryParams } from './useQueryContractItemNetPrice';
import type { ContractItemsQueryParams } from './useQueryContractItems';
import type {
  ContractPartiesQueryParams,
  ContractPartyLookupParams,
} from './useQueryContractParties';
import type { ContractsQueryParams } from './useQueryContracts';

import type { UUID } from '~/types/common';

import { unique } from '@vestigas/utils';

/**
 * Query key factory for contract-related queries.
 */
export const queryKeysContract = {
  base: () => 'contracts' as const,
  get: (contractId: UUID) => [
    queryKeysContract.base(),
    'detail',
    { contractId },
  ],
  getActions: (contractId: UUID) =>
    [queryKeysContract.base(), 'actions', { contractId }] as const,
  getAll: (queryParams?: Partial<ContractsQueryParams>) => {
    const normalized = queryParams
      ? {
          ...queryParams,
          accountingReferenceIds: queryParams.accountingReferenceIds
            ? unique(queryParams.accountingReferenceIds)
                .filter(Boolean)
                .toSorted()
            : undefined,
          siteIds: queryParams.siteIds
            ? unique(queryParams.siteIds).filter(Boolean).toSorted()
            : undefined,
        }
      : queryParams;

    return [queryKeysContract.base(), 'list', normalized].filter(Boolean);
  },
  getArticleSuggestions: (
    queryParams?: Partial<ContractArticleSuggestionsQueryParams>,
  ) =>
    [queryKeysContract.base(), 'article-suggestions', queryParams].filter(
      Boolean,
    ),
  getBulk: (ids: UUID[]) =>
    [
      queryKeysContract.base(),
      'bulk',
      {
        ids: unique([ids].flat().filter(Boolean).toSorted()),
      },
    ] as const,
  getConditionNameSuggestions: (
    queryParams: ContractConditionNameSuggestionsQueryParams,
  ) =>
    [
      queryKeysContract.base(),
      'condition-name-suggestions',
      queryParams,
    ] as const,
  getDuplicateCheck: (
    params: Partial<ContractDuplicateCheckParams> | undefined,
  ) => [queryKeysContract.base(), 'duplicate-check', params ?? {}] as const,
  getFilterParties: (queryParams: Partial<ContractPartiesQueryParams>) =>
    [queryKeysContract.base(), 'filter-parties', 'list', queryParams] as const,
  getFilterParty: (queryParams: ContractPartyLookupParams) =>
    [
      queryKeysContract.base(),
      'filter-parties',
      'detail',
      queryParams,
    ] as const,
  getHistory: (contractId: UUID) =>
    [queryKeysContract.base(), 'history', { contractId }] as const,
  getHistoryVersionItemChanges: ({
    contractId,
    queryParams,
    versionNumber,
  }: {
    contractId: UUID | undefined;
    queryParams: ContractVersionPageQueryParams;
    versionNumber: number | undefined;
  }) =>
    [
      queryKeysContract.base(),
      'history',
      { contractId },
      'item-changes',
      { versionNumber },
      queryParams,
    ] as const,
  getHistoryVersionItems: ({
    contractId,
    queryParams,
    versionNumber,
  }: {
    contractId: UUID | undefined;
    queryParams: ContractVersionPageQueryParams;
    versionNumber: number | undefined;
  }) =>
    [
      queryKeysContract.base(),
      'history',
      { contractId },
      'items',
      { versionNumber },
      queryParams,
    ] as const,
  getItemNetPrice: ({
    contractId,
    itemId,
    queryParams,
  }: {
    contractId: UUID | undefined;
    itemId: UUID | undefined;
    queryParams: ContractItemNetPriceQueryParams;
  }) =>
    [
      queryKeysContract.base(),
      'net-price',
      { contractId, itemId },
      queryParams,
    ] as const,
  getItem: (contractId: UUID | undefined, itemId: UUID | undefined) =>
    [
      queryKeysContract.base(),
      'items',
      { contractId },
      'detail',
      { itemId },
    ] as const,
  // A sibling of getItem, not its prefix: invalidating the lists must not
  // refetch item details (a just-deleted item's detail would answer 404).
  getItems: (
    contractId: UUID,
    queryParams: Partial<ContractItemsQueryParams> | undefined,
  ) =>
    [
      queryKeysContract.base(),
      'items',
      { contractId },
      'list',
      queryParams,
    ].filter(Boolean),
  getPermissions: (contractId: UUID) =>
    [queryKeysContract.base(), 'permissions', { contractId }] as const,
  getPublicView: (contractId: UUID) =>
    [queryKeysContract.base(), 'public-view', { contractId }] as const,
} as const;
